import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, FileText, Save, ImagePlus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);

function ImageUploader({ value, onChange, label, aspect = "16/9", testid }) {
  const [busy, setBusy] = useState(false);
  const inputId = `img-up-${testid || uid()}`;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.uploadProposalImage(file);
      onChange(res.path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      {label && <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5">{label}</label>}
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-white/10 group" style={{ aspectRatio: aspect }} data-testid={`${testid}-preview`}>
          <img src={api.proposalImageUrl(value)} alt="upload preview" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/70 text-white opacity-0 group-hover:opacity-100 transition hover:bg-red-600" data-testid={`${testid}-remove`}><Trash2 size={14} /></button>
        </div>
      ) : (
        <label htmlFor={inputId} className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/15 bg-black/20 cursor-pointer hover:border-primary-500/50 hover:bg-white/5 transition py-6 text-zinc-400" style={{ aspectRatio: aspect }} data-testid={testid}>
          {busy ? <Loader2 size={20} className="animate-spin text-primary-400" /> : <ImagePlus size={20} />}
          <span className="text-xs font-bold">{busy ? "Uploading…" : "Upload photo"}</span>
          <span className="text-[10px] text-zinc-600">PNG · JPG · WEBP · up to 8MB</span>
        </label>
      )}
      <input id={inputId} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

const emptyProposal = () => ({
  title: "",
  client_name: "",
  client_contact: "",
  client_email: "",
  prepared_by: "Dondrae Mills | Eventism & DONDRÁE Agency",
  service_slug: "",
  summary: "",
  hero_image: "",
  sections: [],
  line_items: [],
  passthrough_items: [],
  retainer: 0,
  payment_terms: "",
  status: "draft",
});

export default function ProposalBuilder({ proposal, services = [], onClose, onSaved }) {
  const [p, setP] = useState(() => {
    if (!proposal) return emptyProposal();
    return {
      ...emptyProposal(),
      ...proposal,
      sections: (proposal.sections || []).map((s) => ({ ...s, id: s.id || uid() })),
      line_items: (proposal.line_items || []).map((l) => ({ ...l, id: l.id || uid() })),
      passthrough_items: (proposal.passthrough_items || []).map((x) => ({ ...x, id: x.id || uid() })),
    };
  });
  const [saving, setSaving] = useState(false);
  const [businesses, setBusinesses] = useState([]);

  useEffect(() => {
    api.adminBusinesses().then(setBusinesses).catch(() => {});
  }, []);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const linkBusiness = (id) => {
    const b = businesses.find((x) => x.id === id);
    if (!b) { setP((s) => ({ ...s, client_id: "" })); return; }
    setP((s) => ({
      ...s,
      client_id: b.id,
      client_name: b.business_name || s.client_name,
      client_contact: b.contact_name || s.client_contact,
      client_email: b.email || s.client_email,
    }));
  };
  const total = p.line_items.reduce((sum, li) => sum + ((Number(li.amount) || 0) - (Number(li.discount) || 0)), 0);
  const sigRequired = total > 4500;

  // sections
  const addSection = () => set("sections", [...p.sections, { id: uid(), title: "", subtitle: "", date: "", location: "", description: "", image: "", responsibilities: [] }]);
  const updSection = (id, k, v) => set("sections", p.sections.map((s) => (s.id === id ? { ...s, [k]: v } : s)));
  const delSection = (id) => set("sections", p.sections.filter((s) => s.id !== id));

  // line items
  const addLineItem = (preset) => set("line_items", [...p.line_items, { id: uid(), description: preset?.description || "", amount: preset?.amount || 0, discount: 0 }]);
  const updLineItem = (id, k, v) => set("line_items", p.line_items.map((l) => (l.id === id ? { ...l, [k]: v } : l)));
  const delLineItem = (id) => set("line_items", p.line_items.filter((l) => l.id !== id));

  // passthrough
  const addPass = () => set("passthrough_items", [...p.passthrough_items, { id: uid(), description: "", estimate: "" }]);
  const updPass = (id, k, v) => set("passthrough_items", p.passthrough_items.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const delPass = (id) => set("passthrough_items", p.passthrough_items.filter((x) => x.id !== id));

  const startFromService = (slug) => {
    set("service_slug", slug);
    if (!slug) return;
    const svc = services.find((s) => s.slug === slug);
    if (svc) {
      addLineItem({ description: `${svc.title} — Coordination Fee`, amount: svc.price || 0 });
      if (!p.title) set("title", `Proposal & Agreement — ${svc.title}`);
    }
  };

  const save = async () => {
    if (!p.title.trim()) { toast.error("Proposal title is required"); return; }
    if (p.line_items.length === 0) { toast.error("Add at least one fee line item"); return; }
    setSaving(true);
    try {
      const payload = {
        ...p,
        retainer: Number(p.retainer) || 0,
        sections: p.sections.map((s) => ({
          ...s,
          responsibilities: Array.isArray(s.responsibilities)
            ? s.responsibilities
            : String(s.responsibilities || "").split("\n").map((x) => x.trim()).filter(Boolean),
        })),
        line_items: p.line_items.map((l) => ({ ...l, amount: Number(l.amount) || 0, discount: Number(l.discount) || 0 })),
      };
      const saved = proposal?.id ? await api.updateProposal(proposal.id, payload) : await api.createProposal(payload);
      toast.success(proposal?.id ? "Proposal updated" : "Proposal created");
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save proposal");
    } finally {
      setSaving(false);
    }
  };

  const respText = (s) => (Array.isArray(s.responsibilities) ? s.responsibilities.join("\n") : s.responsibilities || "");
  const inputCls = "w-full px-4 py-2.5 rounded-lg dark-input text-sm";
  const labelCls = "block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="proposal-builder-modal">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-4xl relative fade-in overflow-hidden border border-white/10 flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
          <div className="flex items-center gap-2">
            <FileText size={17} className="text-primary-400" />
            <h3 className="text-base font-bold text-white">{proposal?.id ? "Edit Proposal" : "New Proposal"}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="proposal-builder-close"><X /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 hide-scrollbar">
          {/* Basics */}
          <section className="space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Proposal Details</h4>
            <div>
              <label className={labelCls}>Title *</label>
              <input value={p.title} onChange={(e) => set("title", e.target.value)} placeholder="Proposal & Agreement — F7 Collective Events" className={inputCls} data-testid="proposal-title-input" />
            </div>
            <div>
              <label className={labelCls}>Link to Business Account</label>
              <select value={p.client_id || ""} onChange={(e) => linkBusiness(e.target.value)} className={inputCls} data-testid="proposal-business-select">
                <option value="">— Not linked (enter details manually) —</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.business_name}{b.email ? ` · ${b.email}` : ""}</option>)}
              </select>
              <p className="text-[11px] text-zinc-600 mt-1">Linking connects this proposal to the client's profile so payments and balances sync automatically.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><label className={labelCls}>Client / Company</label><input value={p.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder="F7 Collective" className={inputCls} data-testid="proposal-client-name" /></div>
              <div><label className={labelCls}>Contact Name</label><input value={p.client_contact} onChange={(e) => set("client_contact", e.target.value)} placeholder="Charles" className={inputCls} /></div>
              <div><label className={labelCls}>Contact Email</label><input value={p.client_email} onChange={(e) => set("client_email", e.target.value)} placeholder="client@email.com" className={inputCls} data-testid="proposal-client-email" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Start from a Service (optional)</label>
                <select value={p.service_slug || ""} onChange={(e) => startFromService(e.target.value)} className={inputCls} data-testid="proposal-service-select">
                  <option value="">— None / free-form —</option>
                  {services.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select value={p.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Executive Summary</label>
              <textarea value={p.summary} onChange={(e) => set("summary", e.target.value)} rows={3} placeholder="Outline the partnership and objectives…" className={inputCls} />
            </div>
            <ImageUploader value={p.hero_image} onChange={(path) => set("hero_image", path)} label="Hero Image (banner at top of proposal)" aspect="16/6" testid="proposal-hero-upload" />
          </section>

          {/* Sections */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider">Event / Scope Sections</h4>
              <button onClick={addSection} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center" data-testid="proposal-add-section"><Plus size={12} className="mr-1" /> Add Section</button>
            </div>
            {p.sections.length === 0 && <p className="text-zinc-600 text-sm">No sections yet. Add event or scope blocks (dates, locations, responsibilities).</p>}
            {p.sections.map((s, i) => (
              <div key={s.id} className="p-4 rounded-xl bg-black/30 border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-zinc-400">Section {i + 1}</span>
                  <button onClick={() => delSection(s.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
                <input value={s.title} onChange={(e) => updSection(s.id, "title", e.target.value)} placeholder="Section title (e.g. Teen Water World Day)" className={inputCls} />
                <input value={s.subtitle} onChange={(e) => updSection(s.id, "subtitle", e.target.value)} placeholder="Subtitle / concept (optional)" className={inputCls} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <ImageUploader value={s.image} onChange={(path) => updSection(s.id, "image", path)} label="Section photo (left side)" aspect="4/3" testid={`section-image-${i}`} />
                  </div>
                  <div className="md:col-span-2 space-y-3">
                    <input value={s.date} onChange={(e) => updSection(s.id, "date", e.target.value)} placeholder="Date (e.g. Sep 7, 2026)" className={inputCls} />
                    <input value={s.location} onChange={(e) => updSection(s.id, "location", e.target.value)} placeholder="Location" className={inputCls} />
                  </div>
                </div>
                <textarea value={s.description} onChange={(e) => updSection(s.id, "description", e.target.value)} rows={2} placeholder="Description / logistics…" className={inputCls} />
                <div>
                  <label className={labelCls}>Responsibilities (one per line)</label>
                  <textarea value={respText(s)} onChange={(e) => updSection(s.id, "responsibilities", e.target.value.split("\n"))} rows={2} placeholder={"Charter bus booking\nOn-site logistics oversight"} className={inputCls} />
                </div>
              </div>
            ))}
          </section>

          {/* Line items */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider">Fee Line Items</h4>
              <button onClick={() => addLineItem()} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center" data-testid="proposal-add-lineitem"><Plus size={12} className="mr-1" /> Add Line Item</button>
            </div>
            {p.line_items.length === 0 && <p className="text-zinc-600 text-sm">Add at least one fee. Pick a service above to prefill, then edit freely.</p>}
            <div className="space-y-2">
              {p.line_items.map((l) => {
                const net = (Number(l.amount) || 0) - (Number(l.discount) || 0);
                return (
                  <div key={l.id} className="grid grid-cols-12 gap-2 items-center" data-testid="proposal-lineitem-row">
                    <input value={l.description} onChange={(e) => updLineItem(l.id, "description", e.target.value)} placeholder="Description" className={`${inputCls} col-span-6`} />
                    <input type="number" value={l.amount} onChange={(e) => updLineItem(l.id, "amount", e.target.value)} placeholder="Fee $" className={`${inputCls} col-span-2`} />
                    <input type="number" value={l.discount} onChange={(e) => updLineItem(l.id, "discount", e.target.value)} placeholder="Disc $" className={`${inputCls} col-span-2`} />
                    <div className="col-span-1 text-right text-sm font-bold text-white">${net.toLocaleString()}</div>
                    <button onClick={() => delLineItem(l.id)} className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-end"><Trash2 size={14} /></button>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-primary-500/10 border border-primary-500/20">
              <span className="text-sm font-bold text-white">Total Professional Fee</span>
              <span className="text-lg font-bold text-primary-300" data-testid="proposal-total">${total.toLocaleString()}</span>
            </div>
            {sigRequired && <p className="text-xs text-orange-400">Over $4,500 — the client will be required to sign (name + date) before accepting.</p>}
          </section>

          {/* Pass-through */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider">Pass-Through / Direct-Billed (optional)</h4>
              <button onClick={addPass} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center"><Plus size={12} className="mr-1" /> Add</button>
            </div>
            {p.passthrough_items.map((x) => (
              <div key={x.id} className="grid grid-cols-12 gap-2 items-center">
                <input value={x.description} onChange={(e) => updPass(x.id, "description", e.target.value)} placeholder="e.g. Charter Bus Rental" className={`${inputCls} col-span-7`} />
                <input value={x.estimate} onChange={(e) => updPass(x.id, "estimate", e.target.value)} placeholder="Estimate (e.g. $850–$1,100)" className={`${inputCls} col-span-4`} />
                <button onClick={() => delPass(x.id)} className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-end"><Trash2 size={14} /></button>
              </div>
            ))}
          </section>

          {/* Payment terms */}
          <section className="space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Payment Terms</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Retainer ($)</label>
                <input type="number" value={p.retainer} onChange={(e) => set("retainer", e.target.value)} placeholder="1000" className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Terms / Notes</label>
                <input value={p.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="e.g. $1000 retainer due on acceptance" className={inputCls} />
              </div>
            </div>
          </section>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3 bg-white/5">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10 transition">Cancel</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 transition flex items-center disabled:opacity-60" data-testid="proposal-save-btn">
            <Save size={15} className="mr-2" /> {saving ? "Saving…" : proposal?.id ? "Update Proposal" : "Create Proposal"}
          </button>
        </div>
      </div>
    </div>
  );
}
